import { USER_KIND_USER } from "~/constants/userKinds";
import { isDefined } from "~/shared/common";
import { IconKind, TriggerKind } from "~/shared/enums";
import type { Automation, AutomationUpdate, Task, TaskCreate } from "~/shared/types";
import { makeRandomColorHex } from "~/utils/color";
import { makeDuid } from "~/utils/common";
import { automationComparator } from "~/utils/comparator";
import { getOrdersBetween } from "~/utils/orderManager";

import type { PiniaActionAdaptor, PiniaGetterAdaptor } from "../shared";
import type { DataStore } from ".";
import { removeFromSorted, setObjectMapAndSorted, updateInSorted } from "./utils";

export type State = {
  _duidsToAutomations: Map<string, Automation>;
  _sortedAutomationDuids: string[];
};

export const state: State = {
  _duidsToAutomations: new Map(),
  _sortedAutomationDuids: [],
};

export type Getters = {
  automationList: Automation[];
  getAutomationByDuid: (duid: string) => Automation | undefined;
};

export type Actions = {
  createAutomation: (
    order?: string,
    partialAutomation?: Partial<Automation>,
    options?: { awaitBackend?: boolean }
  ) => Promise<Automation>;
  updateAutomation: (update: AutomationUpdate, options?: { awaitBackend?: boolean }) => Promise<Automation | undefined>;
  deleteAutomation: (automation: Automation, options?: { awaitBackend?: boolean }) => Promise<void>;
  $createAutomationNoBackend: (order?: string, partialAutomation?: Partial<Automation>) => Automation;
  $setAutomations: (automations: Automation[]) => void;
  $createOrUpdateAutomation: (automation: Automation) => void;
  $deleteAutomation: (automation: Automation) => void;
  $updateAutomationsRemapDartboardNoBackend: (dartboardDuidRemap: Map<string, string>) => AutomationUpdate[];
  $getAssignCreatorOnCreateFields: (task: Task | TaskCreate, taskCreate: TaskCreate) => Partial<TaskCreate>;
};

const getters: PiniaGetterAdaptor<Getters, DataStore> = {
  automationList() {
    return this._sortedAutomationDuids.map((e) => this._duidsToAutomations.get(e)).filter(isDefined);
  },
  getAutomationByDuid() {
    return (duid) => this._duidsToAutomations.get(duid);
  },
};

const actions: PiniaActionAdaptor<Actions, DataStore> = {
  async createAutomation(order, partialAutomation = {}, options = {}) {
    const { awaitBackend = false } = options;
    const automation = this.$createAutomationNoBackend(order, partialAutomation);

    const backendAction = this.$backend.automation.create(automation);
    if (awaitBackend) {
      await backendAction;
    }
    return automation;
  },
  async updateAutomation(update, options = {}) {
    const { awaitBackend = false } = options;
    const automation = this.getAutomationByDuid(update.duid);
    if (!automation) {
      return undefined;
    }

    this.$createOrUpdateAutomation({ ...automation, ...update });

    const backendAction = this.$backend.automation.update(update);
    if (awaitBackend) {
      await backendAction;
    }
    return automation;
  },
  async deleteAutomation(automation, options = {}) {
    const { awaitBackend = false } = options;

    this.$deleteAutomation(automation);

    const backendAction = this.$backend.automation.delete(automation.duid);
    if (awaitBackend) {
      await backendAction;
    }
  },
  $createAutomationNoBackend(order, partialAutomation = {}) {
    const firstOrder = this.automationList[0]?.order;
    const automation: Automation = {
      duid: makeDuid(),
      enabled: false,
      order: order ?? getOrdersBetween(undefined, firstOrder)?.[0] ?? "",
      title: `Automation ${this.automationList.length + 1}`,
      description: "",
      triggerKind: TriggerKind.TASK_CREATE,
      currentConditions: [],
      previousConditions: [],
      propertyUpdates: {},
      colorHex: makeRandomColorHex(),
      iconKind: IconKind.NONE,
      iconNameOrEmoji: "",
      ...partialAutomation,
    };

    this.$createOrUpdateAutomation(automation);

    return automation;
  },
  $setAutomations(automations) {
    setObjectMapAndSorted(this._duidsToAutomations, this._sortedAutomationDuids, automations, automationComparator);
  },
  $createOrUpdateAutomation(automation) {
    const currentAutomation = this.getAutomationByDuid(automation.duid);

    if (!currentAutomation) {
      this._duidsToAutomations.set(automation.duid, automation);
    } else {
      Object.assign(currentAutomation, automation);
    }
    updateInSorted(this._duidsToAutomations, this._sortedAutomationDuids, automation.duid, automationComparator);
  },
  $deleteAutomation(automation) {
    this._duidsToAutomations.delete(automation.duid);
    removeFromSorted(this._sortedAutomationDuids, automation.duid);
  },
  $updateAutomationsRemapDartboardNoBackend(dartboardDuidRemap) {
    const dartboardPropertyDuid = this.defaultDartboardProperty.duid;
    const remapDartboardDuid = (value: string) => dartboardDuidRemap.get(value) ?? value;
    const remapConditions = (conditions: Automation["currentConditions"]) => {
      let hasChanges = false;
      const updatedConditions = conditions.map((condition) => {
        if (condition.propertyDuid !== dartboardPropertyDuid) {
          return condition;
        }

        const values = condition.values.map((value) => (typeof value === "string" ? remapDartboardDuid(value) : value));
        if (values.every((value, index) => value === condition.values[index])) {
          return condition;
        }
        hasChanges = true;
        return { ...condition, values };
      });
      return { hasChanges, updatedConditions };
    };

    return this.automationList
      .map((automation): AutomationUpdate | undefined => {
        let hasChanges = false;
        const propertyUpdates = { ...automation.propertyUpdates };
        const updatedDartboardDuid = propertyUpdates[dartboardPropertyDuid];
        if (typeof updatedDartboardDuid === "string") {
          const remappedDartboardDuid = remapDartboardDuid(updatedDartboardDuid);
          if (remappedDartboardDuid !== updatedDartboardDuid) {
            propertyUpdates[dartboardPropertyDuid] = remappedDartboardDuid;
            hasChanges = true;
          }
        }

        const { hasChanges: currentConditionsChanged, updatedConditions: currentConditions } = remapConditions(
          automation.currentConditions
        );
        const { hasChanges: previousConditionsChanged, updatedConditions: previousConditions } = remapConditions(
          automation.previousConditions
        );
        hasChanges ||= currentConditionsChanged || previousConditionsChanged;
        if (!hasChanges) {
          return undefined;
        }

        const update = {
          duid: automation.duid,
          currentConditions,
          previousConditions,
          propertyUpdates,
        };
        this.$createOrUpdateAutomation({ ...automation, ...update });
        return update;
      })
      .filter(isDefined);
  },
  $getAssignCreatorOnCreateFields(task, taskCreate) {
    const tenantStore = this.$useTenantStore();
    const userStore = this.$useUserStore();
    if (!tenantStore.assignCreatorOnCreate || userStore.kind !== USER_KIND_USER || task.drafterDuid !== null) {
      return {};
    }

    return {
      ...(taskCreate.assigneeDuids === undefined && !task.assigneeDuids?.length
        ? { assigneeDuids: [userStore.duid] }
        : {}),
      ...(taskCreate.reviewerDuids === undefined && !task.reviewerDuids?.length
        ? { reviewerDuids: [userStore.duid] }
        : {}),
    };
  },
};

export { actions, getters };
